import type { LucideIcon } from 'lucide-react'

export type StaffNavLeaf = {
  kind: 'leaf'
  id: string
  label: string
  path: string
  icon: LucideIcon
  keywords: string[]
  disabled?: boolean
  activeWhenPrefix?: boolean
  /** Paths that should not activate this leaf even when they sit under `path`. */
  activeExcludePrefixes?: string[]
}

export type StaffNavGroup = {
  kind: 'group'
  id: string
  label: string
  icon: LucideIcon
  keywords: string[]
  children: StaffNavNode[]
}

export type StaffNavNode = StaffNavLeaf | StaffNavGroup

export function isNavGroup(node: StaffNavNode): node is StaffNavGroup {
  return node.kind === 'group'
}

export function leaf(
  id: string,
  label: string,
  path: string,
  icon: LucideIcon,
  keywords: string[],
  options?: {
    disabled?: boolean
    activeWhenPrefix?: boolean
    activeExcludePrefixes?: string[]
  },
): StaffNavLeaf {
  return {
    kind: 'leaf',
    id,
    label,
    path,
    icon,
    keywords,
    ...(options?.disabled !== undefined ? { disabled: options.disabled } : {}),
    ...(options?.activeWhenPrefix !== undefined
      ? { activeWhenPrefix: options.activeWhenPrefix }
      : {}),
    ...(options?.activeExcludePrefixes !== undefined
      ? { activeExcludePrefixes: options.activeExcludePrefixes }
      : {}),
  }
}

/** Whether a leaf should show the active style for the current pathname. */
export function isLeafPathActive(
  pathname: string,
  path: string,
  activeWhenPrefix?: boolean,
  activeExcludePrefixes?: string[],
): boolean {
  if (
    activeExcludePrefixes?.some(
      (ex) => pathname === ex || pathname.startsWith(`${ex}/`),
    )
  ) {
    return false
  }
  if (pathname === path) return true
  if (activeWhenPrefix && pathname.startsWith(`${path}/`)) return true
  return false
}

export function group(
  id: string,
  label: string,
  icon: LucideIcon,
  keywords: string[],
  children: StaffNavNode[],
): StaffNavGroup | null {
  if (children.length === 0) return null
  return { kind: 'group', id, label, icon, keywords, children }
}

export function pushIfPresent(tree: StaffNavNode[], node: StaffNavNode | null) {
  if (node) tree.push(node)
}

export function flattenNavLeaves(nodes: StaffNavNode[]): StaffNavLeaf[] {
  const out: StaffNavLeaf[] = []
  for (const node of nodes) {
    if (node.kind === 'leaf') {
      out.push(node)
    } else {
      out.push(...flattenNavLeaves(node.children))
    }
  }
  return out
}

export function collectNavPaths(node: StaffNavNode): string[] {
  if (node.kind === 'leaf') return [node.path]
  return node.children.flatMap(collectNavPaths)
}

/** Serializable shape for snapshot / regression tests (icons omitted). */
export function serializeNavTree(nodes: StaffNavNode[]): unknown[] {
  return nodes.map((node) => {
    if (node.kind === 'leaf') {
      return {
        kind: 'leaf',
        id: node.id,
        label: node.label,
        path: node.path,
        ...(node.disabled ? { disabled: true } : {}),
        ...(node.activeWhenPrefix ? { activeWhenPrefix: true } : {}),
      }
    }
    return {
      kind: 'group',
      id: node.id,
      label: node.label,
      children: serializeNavTree(node.children),
    }
  })
}

export function countNavLeaves(nodes: StaffNavNode[]): number {
  let count = 0
  for (const node of nodes) {
    if (node.kind === 'leaf') count += 1
    else count += countNavLeaves(node.children)
  }
  return count
}

export function collectNavLabels(nodes: StaffNavNode[]): string[] {
  const labels: string[] = []
  for (const node of nodes) {
    labels.push(node.label)
    if (node.kind === 'group') labels.push(...collectNavLabels(node.children))
  }
  return labels
}
